import { FC, useState } from 'react';
import Slider from 'react-slick';
import styled from 'styled-components';

import leftArrow from 'assets/images/gray-nav-arrow-left.svg';
import rightArrow from 'assets/images/gray-nav-arrow-right.svg';
import navArrow from 'assets/images/nav-green-circle-arrow.svg';

import Heading from 'quarks/Heading';
import Image, { ImageData } from 'quarks/Image';
import Text from 'quarks/Text';

import ComponentButton from 'components/Button/ComponentButton';

import richTextParser from 'utils/richTextParser';

import type { ContentfulComponentCallToAction, ContentfulInPageFeatures } from 'graphqlTypes';

interface InPageFeaturesV2Props extends ContentfulInPageFeatures {
  backgroundColor?: string;
  backgroundIsDark?: boolean;
  cta?: ContentfulComponentCallToAction;
}

const InPageFeaturesV2Desktop: FC<InPageFeaturesV2Props> = ({
  backgroundColor,
  backgroundIsDark,
  inPageNavigationItems,
  cta,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);

  let slider;
  let selNavOffsetTop = 0;
  let selObj;

  const sliderSettings = {
    arrows: true,
    draggable: false,
    touchMove: false,
    speed: 0,
    accessibility: false,
    cssEase: 'cubic-bezier(0.55, 0.085, 0.68, 0.53)',
    afterChange: (index: number) => {
      setActiveIndex(index);
    },
  };

  const onNavItemClick = index => {
    if (index >= 0) {
      setActiveIndex(index);
      slider.slickGoTo(index, true);
    }
  };

  if (typeof window === 'object' && (selObj = document.getElementById(`nav-item-${activeIndex}`))) {
    selNavOffsetTop = selObj.offsetTop;
  }

  const backgroundStyles =
    backgroundColor === 'White' || !backgroundColor ? 'from-gray-100 to-transparent' : 'from-white to-transparent';

  return (
    <Container className="max-w-[1320px] mx-auto my-0" iconTop={selNavOffsetTop} id="features-v2">
      <div className="flex">
        <div className="navigation relative w-[265px] mt-5 mr-10 mb-5">
          {inPageNavigationItems?.map((item, index) => (
            <div
              className={`nav-item nav-item-${index} hover:cursor-pointer ${
                index === activeIndex ? ' selected-item' : ''
              }`}
              id={`nav-item-${index}`}
              onClick={() => onNavItemClick(index)}
              key={index}
              role="presentation"
            >
              <Heading
                as="h2"
                textStyle="h5"
                textColor={index === activeIndex ? 'green-300' : backgroundIsDark ? 'common-white' : 'gray-900'}
                textAlign="center"
                className="!text-right !mb-5 !font-medium"
              >
                {item?.title}
              </Heading>
            </div>
          ))}
          {cta && (
            <ComponentButton
              key={cta?.contentful_id}
              {...cta}
              label={cta?.label || 'Get Demo'}
              buttonColor="Garden 300"
              className="!absolute right-0 bottom-0"
            />
          )}
          <img src={navArrow} id="arrow-circle-right" alt="navigation-arrow" />
        </div>

        <div
          className={`flex-1 block relative overflow-hidden bg-gradient-to-r ${backgroundStyles} pt-10 pl-10 pr-10 pb-20`}
        >
          <Slider ref={c => (slider = c)} {...sliderSettings}>
            {inPageNavigationItems?.map((item, index) => {
              const { featuredImage } = item;
              const image = featuredImage?.bynderImageDesktop || featuredImage?.imageDesktop;

              return (
                <div className="" key={index}>
                  <div className="featured-content">
                    <div className="max-h-[400px] flex justify-center w-full">
                      {featuredImage && <Image image={image as ImageData} alt="featured" />}
                    </div>
                    {item.description && (
                      <div className="mt-10">
                        <Text as="p" textStyle="textSm" textColor="gray-900" textAlign="center">
                          {richTextParser(item.description)}
                        </Text>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </Slider>
        </div>
      </div>
    </Container>
  );
};

export default InPageFeaturesV2Desktop;

export const Container = styled.div`
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-bottom: 15px;
    font-size: 20px;
  }

  img {
    max-height: 400px;
    width: auto;
    justify-self: center;
    margin: 0 auto;
  }

  .gatsby-image-wrapper {
    max-height: 400px;
    width: 100%;
  }

  .navigation {
    img {
      position: absolute;
      right: -58px;
      top: ${({ iconTop }) => -3 + iconTop}px;
      transition: top 0.25s cubic-bezier(0.55, 0.085, 0.68, 0.53) 0s;
      z-index: 100;
      height: 30px;
      width: 30px;
    }
  }

  .slick-prev,
  .slick-next {
    bottom: -80px;
    border-radius: 50%;
    top: auto;
    width: 45px;
    height: 45px;
    z-index: 1;
    box-shadow: 0 3px 6px 1px rgba(160, 161, 188, 0.35);

    &:before {
      width: 45px;
      height: 45px;
      color: transparent;
      content: '' !important;
    }

    &:hover {
      cursor: pointer;
    }
  }

  .slick-prev {
    right: 60px;
    left: inherit;
    background-image: url('${leftArrow}') !important;
    background-color: var(--white) !important;
    background-repeat: no-repeat !important;
    background-size: 11px !important;
    background-position: center !important;
    position: absolute;
  }

  .slick-next {
    right: 0;
    background-image: url('${rightArrow}') !important;
    background-color: var(--white) !important;
    background-repeat: no-repeat !important;
    background-size: 11px !important;
    background-position-x: 19px !important;
    background-position-y: center !important;
    position: absolute;
  }
`;
